import * as React from 'react'

import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUIUtcTime } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'

import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import type ShiftConfigForm from 'modules/shift_management/lib/forms/ShiftConfigForm'
import { trackAddCopyShiftsToAllTemplateShiftManagement } from 'modules/shift_management/lib/trackEvents'
import type ShiftConfiguration from 'modules/shift_management/store/api/models/ShiftConfiguration'
import {
  DAY_TYPE,
  shiftDaysSortedByStartingWeekday,
} from 'modules/shift_management/store/api/models/ShiftConfiguration'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

import {
  ShiftConfigWithBreaksForm,
  blankShiftForm,
  copyShiftForm,
  initialBlankForm,
  shiftFormWithBreaks,
} from 'modules/shift_management/components/Settings/ShiftTemplates/Content/EditTemplateWeek/WeekSettings/WeekConfiguration/forms'
import type TemplateWeek from 'modules/shift_management/store/api/models/TemplateWeek'

const useShiftConfigurations = (
  shiftsByWeek: ShiftConfiguration[],
  templateWeek: TemplateWeek,
  shiftTemplateId: number
) => {
  const { loading, plannedBreaksCollection } = useGetPlannedBreaks({
    variables: { shiftConfigurationIds: shiftsByWeek.map((c) => c.id) },
    skip: !shiftsByWeek.length,
  })
  const templateWeekColor = templateWeek.attributes.color
  const canUseRestDays = hasFeature('shift_management_rest_day')

  const groupedByDay = groupBy(shiftsByWeek, (shift) => shift.get('weekday'))

  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()
  const { isTimeFormat24H } = useIsTimeFormat24H()

  const shiftDaysAdjustedByEmployee = useMemo(() => {
    return shiftDaysSortedByStartingWeekday(firstDayOfWeek)
  }, [firstDayOfWeek])

  const getBreakForms = (configurationId: number, isTimeFormat24H: boolean): PlannedBreakForm[] => {
    const collection: PlannedBreak[] = plannedBreaksCollection.filterByShiftConfigurations([
      configurationId,
    ])
    return collection.map((item) => {
      const { id, startAt, endAt, breakType, duration, breakConfiguration } = item

      return new PlannedBreakForm(
        {
          id,
          start_at: startAt && dateBackendDateTimeToUIUtcTime(startAt),
          end_at: endAt && dateBackendDateTimeToUIUtcTime(endAt),
          break_type: breakType,
          duration: duration,
          'break_configuration.id': breakConfiguration?.id,
          'break_configuration.name': breakConfiguration?.name,
          'break_configuration.paid': breakConfiguration?.paid,
          'break_configuration.archived': breakConfiguration?.archived,
        },
        isTimeFormat24H
      )
    })
  }
  const createShiftConfigForms = () => {
    return shiftDaysAdjustedByEmployee.flatMap((day: number) => {
      const weekday = day.toString()
      const shiftsByDay = groupedByDay[weekday]

      if (!shiftsByDay) return initialBlankForm(day, canUseRestDays, Boolean(shiftsByWeek.length))

      return shiftsByDay.map((shift) => {
        const breaks: PlannedBreakForm[] = getBreakForms(shift.id, isTimeFormat24H)
        return shiftFormWithBreaks(day, shift, breaks)
      })
    })
  }

  useEffect(() => {
    if (loading) return

    setShiftConfigForms(createShiftConfigForms())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannedBreaksCollection.plannedBreaks.length, loading])

  const [shiftConfigForms, setShiftConfigForms] =
    React.useState<ShiftConfigWithBreaksForm[]>(createShiftConfigForms())

  const shiftDays = shiftConfigForms.filter((form) => form.shiftConfig.isShift).length
  const restDays = shiftConfigForms.filter((form) => form.shiftConfig.isRestDay).length
  const emptyDays = shiftConfigForms.filter((form) => form.shiftConfig.isEmptyCell).length

  const onAddShift = (day: number) => {
    const configsByDay = shiftConfigForms.filter(({ shiftConfig: { weekday } }) => +weekday === day)
    const firstShiftForm = configsByDay[0]
    const firstShiftConfig = firstShiftForm?.shiftConfig
    const isNotShiftDay =
      configsByDay.length === 1 && (firstShiftConfig?.isRestDay || firstShiftConfig?.isEmptyCell)

    if (isNotShiftDay) {
      if (!firstShiftForm) return

      const index = shiftConfigForms.indexOf(firstShiftForm)
      const updatedShiftForm = blankShiftForm(day)
      updatedShiftForm.shiftConfig.get('start_at').set('')
      updatedShiftForm.shiftConfig.get('end_at').set('')
      const updatedShiftConfigForms = shiftConfigForms.map((forms, i) => {
        return i === index ? updatedShiftForm : forms
      })
      return setShiftConfigForms(updatedShiftConfigForms)
    }
    const newShiftForm = blankShiftForm(day)
    setShiftConfigForms([...shiftConfigForms, newShiftForm])
  }

  const onDeleteShift = async (shiftForm: ShiftConfigForm) => {
    const day = shiftForm.get('weekday').value

    const shiftsByDay = shiftConfigForms.filter((form) => form.shiftConfig.weekday === day)

    if (shiftsByDay.length > 1) {
      const updatedForms = shiftConfigForms.filter((form) => form.shiftConfig !== shiftForm)
      setShiftConfigForms(updatedForms)
    } else {
      const dayType = canUseRestDays ? DAY_TYPE.restDay : ''
      shiftForm.get('id').set('')
      shiftForm.get('start_at').set('')
      shiftForm.get('end_at').set('')
      shiftForm.get('day_type').set(dayType)
      shiftForm.cleanAll()
    }
  }

  const onAddRestDay = (weekday: number) => {
    const restDayForm = blankShiftForm(+weekday)
    restDayForm.shiftConfig.get('day_type').set(DAY_TYPE.restDay)
    const shiftForms = shiftConfigForms.filter((form) => +form.shiftConfig.weekday !== weekday)
    setShiftConfigForms([...shiftForms, restDayForm])
  }

  const onAddEmptyCell = (weekday: number) => {
    const emptyDayForm = blankShiftForm(+weekday)
    emptyDayForm.shiftConfig.get('day_type').set(DAY_TYPE.emptyCell)
    const shiftForms = shiftConfigForms.filter((form) => +form.shiftConfig.weekday !== weekday)
    setShiftConfigForms([...shiftForms, emptyDayForm])
  }

  const onCopyFirst = (shiftForms: ShiftConfigWithBreaksForm[]) => {
    const copiedForms = shiftForms.flatMap((form: ShiftConfigWithBreaksForm) => {
      return shiftDaysAdjustedByEmployee.map((day: number) => {
        return copyShiftForm(day, form)
      })
    })
    setShiftConfigForms(copiedForms)
    const color = templateWeekColor ? nameFromHexCode(templateWeekColor) : null
    trackAddCopyShiftsToAllTemplateShiftManagement({
      templateId: shiftTemplateId,
      weekId: templateWeek.id,
      workingDays: shiftDays - emptyDays,
      restDays: restDays,
      emptyDays: emptyDays,
      color,
    })
  }

  const onAddBreak = (
    breakConfiguration: BreakConfigurationFragment,
    breakType: BreakType,
    shiftConfig: ShiftConfigWithBreaksForm,
    isTimeFormat24H: boolean
  ) => {
    const { name, id, paid } = breakConfiguration
    const form = new PlannedBreakForm(
      {
        'break_configuration.id': id,
        'break_configuration.name': name,
        'break_configuration.paid': paid,
        break_type: breakType,
      },
      isTimeFormat24H
    )

    track(trackingEvents.shiftManagement.addBreakSubmit, { from: 'rotating-shifts' })

    const newConfigs = shiftConfigForms.map((shiftConfigForm: ShiftConfigWithBreaksForm) => {
      if (shiftConfigForm === shiftConfig) shiftConfigForm.breaks.push(form)
      return shiftConfigForm
    })

    setShiftConfigForms(newConfigs)
  }

  const onDeleteBreak = (
    shiftConfigurationForm: ShiftConfigWithBreaksForm,
    plannedBreakForm: PlannedBreakForm
  ) => {
    const { shiftConfig } = shiftConfigurationForm
    const config = shiftConfigForms.find((form) => form.shiftConfig === shiftConfig)

    if (!config) return

    const newConfigs = shiftConfigForms.map((shiftConfigForm) => {
      if (shiftConfigForm.shiftConfig === shiftConfig) {
        shiftConfigForm.breaks = shiftConfigForm.breaks.filter(
          (breakForm: PlannedBreakForm) => breakForm !== plannedBreakForm
        )
      }
      return shiftConfigForm
    })

    setShiftConfigForms(newConfigs)
  }

  return {
    shiftConfigForms,
    onAddShift,
    onDeleteShift,
    onCopyFirst,
    onAddBreak,
    onDeleteBreak,
    onAddRestDay,
    onAddEmptyCell,
  }
}

export default useShiftConfigurations
